<template>
  <div
    v-if="layerShowCardSelection"
    ref="rootRef"
    class="select-cards-wrapper"
    :class="['mode-' + mode, { reduced: reducedMotion, compact, quiet, resting, 'has-inherited': inherited.length > 0 }]"
  >
    <audio-player ref="audioPlayerRef"></audio-player>

    <template v-if="focusActive">
      <!-- The gift room's pull (DESIGN-2 §35): the question to hold, under its
           eyebrow, in view while they choose. -->
      <p v-if="hold_eyebrow" class="focus-eyebrow" data-testid="picker-hold-eyebrow">{{ hold_eyebrow }}</p>
      <div class="focus-message" :class="{ held: !!hold_eyebrow }">{{ hold_eyebrow ? heldQuestion : selectCardsFocusMessage }}</div>

      <!-- The deck. Arc Fan on a wide screen with a mouse; the Sweeping Spread
           (shallow arc, snap scroll, tap to peek / tap again to draw) on
           phones and tablets. utils/card-picker.js holds the rules. -->
      <div
        v-show="showCardDeck"
        ref="stageRef"
        class="picker-stage"
        :class="'mode-' + mode"
        @pointermove="onStagePointer"
        @pointerleave="onStageLeave"
        @pointerdown="touched"
        @click="onStageClick"
        @keydown="onStageKey"
      >
        <div class="ambient-sigil" aria-hidden="true"></div>

        <!-- Arc Fan -->
        <div v-if="mode === 'fan'" class="fan" role="group" aria-label="The deck">
          <div
            v-for="(card, i) in cards"
            :key="card.card_id || card.name"
            class="card"
            :class="{ lift: i === focusIndex, 'back-loading': !backLoaded }"
            role="button"
            tabindex="0"
            :aria-label="`Card ${i + 1} of ${cards.length}`"
            :style="fanStyle(i)"
            @click="onCardClick(card, i, $event)"
            @keydown.enter.prevent="drawCard(card, $event)"
            @keydown.space.prevent="drawCard(card, $event)"
            @focus="focusIndex = i"
          >
            <img v-if="cardBackImageUrl" class="card-back" :src="cardBackImageUrl" alt="" draggable="false" @load="onBackLoad" @error="onBackError" />
          </div>
        </div>

        <!-- Sweeping Spread -->
        <div
          v-else
          ref="scrollerRef"
          class="ribbon-scroller"
          @scroll.passive="onRibbonScroll"
          @pointerdown="onRibbonDown"
        >
          <div class="ribbon-rail" :style="ribbonRailStyle">
            <div
              v-for="(card, i) in cards"
              :key="card.card_id || card.name"
              class="card"
              :class="{ peek: isPeeked(card), 'back-loading': !backLoaded }"
              role="button"
              tabindex="0"
              :aria-label="isPeeked(card) ? 'Tap again to draw this card' : `Card ${i + 1} of ${cards.length}`"
              :style="ribbonStyle(i, card)"
              @click="onCardClick(card, i, $event)"
              @keydown.enter.prevent="onCardClick(card, i, $event)"
              @keydown.space.prevent="onCardClick(card, i, $event)"
            >
              <img v-if="cardBackImageUrl" class="card-back" :src="cardBackImageUrl" alt="" draggable="false" @load="onBackLoad" @error="onBackError" />
            </div>
          </div>
        </div>

      </div>

      <!-- What has been drawn: someone else's cards face up (Draw deeper),
           then this seeker's slots, filled FACE DOWN. The faces belong to the
           reading's own reveal (and its paywall) — never to the picker. -->
      <div class="picker-foot">
        <!-- Feedback for someone stuck, not a standing instruction (§36). -->
        <p v-if="idleHintShown" class="idle-hint" data-testid="picker-idle-hint">{{ idle_hint }}</p>
        <div ref="trayRef" class="tray" data-testid="tray">
          <div v-if="inherited.length" class="tray-group" data-testid="tray-inherited">
            <div v-if="!quiet" class="tray-group-label">{{ inherited_label || 'Already drawn' }}</div>
            <div class="tray-row">
              <figure v-for="(c, i) in inherited" :key="'inh' + i" class="tray-card inherited" :class="{ reversed: c.reversed }">
                <img v-if="c.image_url" :src="c.image_url" :alt="c.name" />
                <figcaption>{{ c.name }}</figcaption>
                <small v-if="c.position_label">{{ c.position_label }}</small>
              </figure>
            </div>
          </div>
          <span v-if="quiet && inherited.length" class="tray-sep" data-testid="tray-sep" aria-hidden="true">✦</span>
          <div class="tray-group">
            <div v-if="inherited.length && !quiet" class="tray-group-label">{{ own_label || 'Yours' }}</div>
            <div class="tray-row" :class="{ lone: slots.length === 1 && !inherited.length, 'two-line': slots.length > 1 && slots.some(s => isWords(s.label)) }">
              <div
                v-for="slot in slots"
                :key="'slot' + slot.index"
                :ref="el => setSlotRef(slot.index, el)"
                class="tray-slot"
                :class="{ filled: slot.filled, 'back-loading': slot.filled && !backLoaded }"
                data-testid="tray-slot"
              >
                <img v-if="slot.filled && cardBackImageUrl" :src="cardBackImageUrl" alt="A drawn card, face down" />
                <span v-else class="slot-star" aria-hidden="true">✧</span>
                <small v-if="slot.label" class="slot-label" :class="{ words: slots.length > 1 && isWords(slot.label) }">{{ slot.label }}</small>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!quiet || (mode === 'ribbon' && peekedCard)" class="picker-status">
          <!-- One card for the gift room (§36): no counter, no Shuffle. -->
          <template v-if="!quiet">
            <span class="pips" aria-hidden="true">
              <span v-for="slot in slots" :key="'pip' + slot.index" class="pip" :class="{ filled: slot.filled }"></span>
            </span>
            <span class="count-text" data-testid="drawn-count" aria-live="polite">
              <!-- Paced draws (2026-10-23): while the last card is read the deck rests. -->
              <template v-if="resting">{{ restingText }}</template>
              <template v-else><b>{{ drawnCount }}</b> of <b>{{ number_of_cards_to_select }}</b><span class="count-word"> drawn</span></template>
            </span>
          </template>
          <!-- The peeked card's commit sits here, under the thumb and clear
               of the card it names (floating it over the deck hid the card). -->
          <button
            v-if="mode === 'ribbon' && peekedCard && !resting"
            type="button"
            class="peek-draw"
            data-testid="peek-draw"
            @click.stop="drawCard(peekedCard, $event)"
          >{{ compact ? 'Draw' : 'Draw this card' }}</button>
          <button
            v-else-if="shuffleAllowed && !quiet"
            type="button"
            class="shuffle"
            data-testid="shuffle"
            @click="shuffle"
          >↺ Shuffle</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { static_store, store } from '@/store';
import axios from 'axios';
import AudioPlayer from "@/components/ui/AudioPlayer.vue";
import { lock_interface, selectCardsFocusMessage } from "@/utils/reader-conversation";
import { restingLine } from '@/utils/reveal-pacing';
import { DEFAULT_CARD_BACK, nextCardBack } from '@/utils/card-back';
import {
  dealDeck, shuffleDeck, pickerMode, tapCard, oneTouchDraw, touchIsScroll, canShuffle, traySlots, inheritedTray,
  fanLayout, ribbonPose, onFanBand, isCompact,
  fanPointerIndex, stickyIndex, fanHoverPose, fanZ, stepToward,
} from '@/utils/card-picker';

const mq = (q) => (typeof window !== 'undefined' && typeof window.matchMedia === 'function')
  ? window.matchMedia(q) : null;

export default {
  emits: ['card-selected', 'interfaceTrigger', 'card-lift'],
  components: { AudioPlayer },
  props: {
    state: [Object, String],
    layerShowCardSelection: {
      type: Boolean,
      required: true,
    },
    showCardDeck: {
      type: Boolean,
      required: true,
    },
    number_of_cards_to_select: {
      type: Number,
      required: true
    },
    // Cards already pulled on a resumed mid-draw reading ([{card_id, name}]).
    // The picker seeds its running count to this length (so the next pick lands
    // at the correct absolute card_order) and hides these from the deck.
    resume_picked: {
      type: Array,
      default: () => [],
    },
    // The spread's position names, one per card to draw — shown under the
    // tray slots when they match the count.
    slot_labels: {
      type: Array,
      default: () => [],
    },
    // Cards someone else already drew ([{name, image_url, position_label,
    // reversed}]) — e.g. the sender's spread beneath a Draw deeper. Shown face
    // up ahead of this seeker's slots; they never count toward the draw.
    inherited_cards: {
      type: Array,
      default: () => [],
    },
    inherited_label: { type: String, default: '' },
    own_label: { type: String, default: '' },
    // The gift room's one-card pull (DESIGN-2 §35, §36): the pill's eyebrow
    // ("Hold this question"); `quiet` drops the counter, Shuffle and the
    // group labels; `idle_hint` fades in under the fan after `idle_ms` idle.
    hold_eyebrow: { type: String, default: '' },
    quiet: { type: Boolean, default: false },
    idle_hint: { type: String, default: '' },
    idle_ms: { type: Number, default: 8000 },
    // Paced draws (2026-10-23): after a pick the deck rests (dimmed, taps
    // ignored) until that card's headline has landed. ReaderPage decides.
    resting: { type: Boolean, default: false },
    // Card ids that must not be dealt (the inherited cards, usually).
    exclude_card_ids: {
      type: Array,
      default: () => [],
    },
    // A lesson's draw (2026-10-28): the picked card is handed up ('card-lift',
    // where it lay) to settle into its panel, instead of flying to the tray.
    lift_out: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const cards = ref([]);
    const cardBackImageUrl = ref(DEFAULT_CARD_BACK);
    // N3 (2026-11-02): a painted placeholder back until the back has loaded;
    // a failed back falls back to the default, then to the placeholder alone.
    const backLoaded = ref(false);
    const onBackLoad = () => { backLoaded.value = true; };
    const onBackError = () => { backLoaded.value = false; cardBackImageUrl.value = nextCardBack(cardBackImageUrl.value); };
    let revealedCards = ref([]);
    let showRevealedCards = ref(false);
    let selectedCards = ref([]);
    const audioPlayerRef = ref(null);
    const rootRef = ref(null);
    const stageRef = ref(null);
    const scrollerRef = ref(null);
    const trayRef = ref(null);

    // ── Layout: which picker, and how big the stage is ──
    const hover = ref(true);
    const reducedMotion = ref(false);
    const width = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);
    const stageW = ref(0);
    const stageH = ref(0);
    const rootH = ref(0);
    // The drawn-card slots, from the stage's top: the Arc Fan's legs come
    // down to their bottom and frame them (null: no slots measured).
    const slotsBox = ref(null);
    const compact = computed(() => isCompact(rootH.value));
    const mode = computed(() => pickerMode({ width: width.value, hover: hover.value }));

    const measure = () => {
      const root = rootRef.value;
      width.value = (root && root.clientWidth) || (typeof window !== 'undefined' ? window.innerWidth : 1024);
      rootH.value = (root && root.clientHeight) || 0;
      const st = stageRef.value;
      stageW.value = (st && st.clientWidth) || width.value;
      stageH.value = (st && st.clientHeight) || 300;
      // Not in a compact pane: there the count and buttons share the
      // tray's row, right where the legs would come down.
      const tray = isCompact(rootH.value) ? null : trayRef.value;
      const slotEls = tray ? tray.querySelectorAll('.tray-slot, .tray-card') : [];
      if (st && tray && slotEls.length && st.getBoundingClientRect) {
        const s = st.getBoundingClientRect();
        const t = tray.getBoundingClientRect();
        const cx = s.left + s.width / 2;
        let bottom = -Infinity;
        slotEls.forEach((el) => { bottom = Math.max(bottom, el.getBoundingClientRect().bottom); });
        slotsBox.value = (s.height > 0 && bottom > s.bottom) ? {
          floor: bottom - s.top,
          halfW: Math.max(cx - t.left, t.right - cx) + 10,
          h: bottom - t.top + 6,
        } : null;
      } else {
        slotsBox.value = null;
      }
    };
    let ro = null;
    let hoverMq = null;
    let motionMq = null;
    const readMedia = () => {
      hover.value = hoverMq ? !!hoverMq.matches : true;
      reducedMotion.value = motionMq ? !!motionMq.matches : false;
    };
    onMounted(() => {
      hoverMq = mq('(hover: hover) and (pointer: fine)');
      motionMq = mq('(prefers-reduced-motion: reduce)');
      readMedia();
      [hoverMq, motionMq].forEach(m => m && m.addEventListener && m.addEventListener('change', readMedia));
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => measure());
      }
      window.addEventListener('resize', measure);
      nextTick(observe);
      // The focus message can arrive before the picker mounts (from her card
      // straight into her own reading, recipient journey R5.1 R5.2): the
      // watcher below only sees changes, so deal now.
      if (selectCardsFocusMessage.value && selectCardsFocusMessage.value.trim() !== '') getCardDeck();
    });
    const observe = () => {
      measure();
      if (!ro) return;
      ro.disconnect();
      if (rootRef.value) ro.observe(rootRef.value);
      if (stageRef.value) ro.observe(stageRef.value);
      if (trayRef.value) ro.observe(trayRef.value);
    };
    onBeforeUnmount(() => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
      [hoverMq, motionMq].forEach(m => m && m.removeEventListener && m.removeEventListener('change', readMedia));
      cancelGlide();
      if (zRaf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(zRaf);
      if (ptrRaf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(ptrRaf);
    });

    // ── Draw state ──
    const focusActive = computed(() => !!(selectCardsFocusMessage.value && selectCardsFocusMessage.value.trim() !== ''));
    const heldQuestion = computed(() => {
      const q = String(selectCardsFocusMessage.value || '').trim().replace(/^["“]+|["”]+$/g, '');
      return q ? `“${q}”` : '';
    });
    // The idle hint (§36): it waits, shows once someone is stuck, and goes
    // the moment they touch the deck.
    const idleHintShown = ref(false);
    let idleTimer = null;
    const armIdle = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = null;
      if (!props.idle_hint || !focusActive.value) return;
      idleTimer = setTimeout(() => { idleHintShown.value = true; }, props.idle_ms);
    };
    const touched = () => { idleHintShown.value = false; armIdle(); };
    watch(focusActive, (on) => {
      idleHintShown.value = false;
      if (on) armIdle();
      else if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
    }, { immediate: true });
    onBeforeUnmount(() => { if (idleTimer) clearTimeout(idleTimer); });
    const drawnCount = computed(() => Math.min(selectedCards.value.length, props.number_of_cards_to_select));
    const full = computed(() => selectedCards.value.length >= props.number_of_cards_to_select);
    const shuffleAllowed = computed(() => canShuffle({ drawn: selectedCards.value.length }) && cards.value.length > 1);
    const slots = computed(() => traySlots({
      count: props.number_of_cards_to_select, picked: selectedCards.value, labels: props.slot_labels,
    }));
    const inherited = computed(() => inheritedTray(props.inherited_cards));
    const peekedId = ref(null);
    const peekedCard = computed(() => cards.value.find(c => (c.card_id ?? c.name) === peekedId.value) || null);
    const isPeeked = (card) => peekedId.value !== null && (card.card_id ?? card.name) === peekedId.value;
    // A slot name of more than one word may wrap, between its words only.
    const isWords = (label) => /\S\s+\S/.test(String(label || '').trim());

    // The card being read is the last one drawn: name it by its position.
    const restingText = computed(() => {
      const labels = Array.isArray(props.slot_labels) ? props.slot_labels : [];
      const i = selectedCards.value.length - 1;
      return restingLine(labels.length === props.number_of_cards_to_select && i >= 0 ? labels[i] : '');
    });
    watch(() => props.resting, (on) => { if (on) peekedId.value = null; });

    const selectCard = (cardName) => {
      if (props.resting) return;
        if (audioPlayerRef.value) {
          audioPlayerRef.value.playSelectCardSound();
        }
      const index = cards.value.findIndex(card => card.name === cardName);

      // If the card is found in the deck
      if (index !== -1) {
        const selectedCard = {
          card_id:cards.value[index].card_id,
          name: cards.value[index].name,
          reversed: cards.value[index].reversed,
          description: cards.value[index].description,
          reverse_description: cards.value[index].reverse_description,
          image_path: cards.value[index].image_path
        }
        revealedCards.value.push(cards.value[index]);
        selectedCards.value.push(selectedCard);
        showRevealedCards.value = true
        cards.value.splice(index, 1);
        peekedId.value = null;
        if (focusIndex.value >= cards.value.length) focusIndex.value = cards.value.length - 1;

        if (selectedCards.value.length >= props.number_of_cards_to_select) {
          lock_interface.value = false
          selectCardsFocusMessage.value = ""
          // Variant-aware load trigger. The N-card-draw architecture
          // (Phase 1) lets a single ReadingSkill state class handle
          // 1/3/4-card spreads — but the FE needs to mount the right
          // component (OneCardReading vs FourCardReading). Emit the
          // generic name with card_count so ReaderPage can route on
          // it. The legacy event name is kept as a second emission
          // for backward compat with any older listener until prod
          // confirms nothing else binds to it.
          const cardCount = props.number_of_cards_to_select
          emit('interfaceTrigger', 'load_card_session_reading', { card_count: cardCount })
          // Per-spread parallel rails (project_spread_recipe): each
          // spread fires its own load_<key>_session_reading so its
          // mount handler can own its layout-specific pane heights
          // and flags without conditionals creeping in here.
          if (cardCount === 1) {
            emit('interfaceTrigger', 'load_1_card_session_reading')
          }
          if (cardCount === 4) {
            emit('interfaceTrigger', 'load_4_card_session_reading')
          }
        }
      emit('card-selected', selectedCard, selectedCards.value.length);
      }
    };

    // A pick the server never received: put the card back in the deck and
    // uncount it, so the next pick carries the right card_order and the
    // completion gate trips on the real last card.
    const undoPick = (card_id) => {
      const i = selectedCards.value.findIndex(c => c.card_id === card_id);
      if (i === -1) return;
      selectedCards.value.splice(i, 1);
      const r = revealedCards.value.findIndex(c => c.card_id === card_id);
      const card = r !== -1 ? revealedCards.value.splice(r, 1)[0] : null;
      if (card) cards.value.push(card);
      showRevealedCards.value = revealedCards.value.length > 0;
      lock_interface.value = true;
    };

    // ── Drawing, with the flight to the tray ──
    const slotEls = [];
    const setSlotRef = (i, el) => { slotEls[i] = el; };
    const flyToTray = (fromEl, slotIndex) => {
      if (reducedMotion.value || !fromEl || typeof document === 'undefined') return;
      const to = slotEls[slotIndex];
      if (!to || typeof fromEl.getBoundingClientRect !== 'function') return;
      const a = fromEl.getBoundingClientRect();
      const b = to.getBoundingClientRect();
      if (!a.width || !b.width) return;
      const ghost = document.createElement('div');
      ghost.className = 'picker-ghost';
      ghost.style.cssText = `position:fixed;left:${a.left}px;top:${a.top}px;width:${a.width}px;height:${a.height}px;`
        + 'z-index:2000;pointer-events:none;border-radius:8px;overflow:hidden;'
        + 'box-shadow:0 0 26px rgba(255,223,163,.6),0 12px 30px rgba(0,0,0,.6);';
      ghost.innerHTML = `<img src="${cardBackImageUrl.value}" style="width:100%;height:100%;object-fit:cover;display:block" alt="">`;
      document.body.appendChild(ghost);
      if (typeof ghost.animate !== 'function') { ghost.remove(); return; }
      const dx = b.left + b.width / 2 - (a.left + a.width / 2);
      const dy = b.top + b.height / 2 - (a.top + a.height / 2);
      const s = b.width / a.width;
      const anim = ghost.animate([
        { transform: 'translate(0,0) scale(1)', opacity: 1 },
        { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 40}px) scale(${(1 + s) / 2 * 1.08})`, opacity: 1, offset: 0.55 },
        { transform: `translate(${dx}px, ${dy}px) scale(${s})`, opacity: 0.2 },
      ], { duration: 620, easing: 'cubic-bezier(.4,0,.2,1)' });
      anim.onfinish = () => ghost.remove();
      anim.oncancel = () => ghost.remove();
    };

    // Where the picked card lies: its box, its own size and its tilt in the fan.
    const liftFrom = (el) => {
      if (!el || typeof el.getBoundingClientRect !== 'function') return { image: cardBackImageUrl.value };
      const b = el.getBoundingClientRect();
      let angle = 0;
      try {
        const m = (getComputedStyle(el).transform || '').match(/^matrix\(([^,]+),\s*([^,]+)/);
        if (m) angle = Math.atan2(parseFloat(m[2]), parseFloat(m[1])) * 180 / Math.PI;
      } catch (e) { /* upright */ }
      return { left: b.left, top: b.top, width: b.width, height: b.height,
        cardWidth: el.offsetWidth || b.width, cardHeight: el.offsetHeight || b.height, angle, image: cardBackImageUrl.value };
    };
    const cardElFrom = (ev) => (ev && ev.currentTarget && ev.currentTarget.classList && ev.currentTarget.classList.contains('card'))
      ? ev.currentTarget : null;
    const drawCard = (card, ev) => {
      if (!card || full.value || props.resting) return;
      const slotIndex = selectedCards.value.length;
      let el = cardElFrom(ev);
      if (!el && stageRef.value) el = stageRef.value.querySelector('.card.peek, .card.lift');
      if (props.lift_out) emit('card-lift', liftFrom(el));
      else flyToTray(el, slotIndex);
      selectCard(card.name);
    };

    const onCardClick = (card, i, ev) => {
      if (suppressClick) { suppressClick = false; return; }
      if (props.resting) return;
      const r = tapCard({ mode: mode.value, peeked: peekedId.value, cardId: card.card_id ?? card.name, full: full.value,
        oneTouch: oneTouchDraw({ quiet: props.quiet, count: props.number_of_cards_to_select }) });
      if (r.action === 'draw') {
        // In the fan the pointer, not the DOM stacking, says which sliver is
        // meant: draw the lifted card if there is one.
        const target = (mode.value === 'fan' && focusIndex.value >= 0 && cards.value[focusIndex.value] && ev && ev.type === 'click')
          ? cards.value[focusIndex.value] : card;
        const el = target === card ? cardElFrom(ev) : null;
        drawCard(target, el ? ev : null);
      } else if (r.action === 'peek') {
        peekedId.value = r.peeked;
        centerCard(i);
      }
    };

    const shuffle = () => {
      if (!shuffleAllowed.value) return;
      cards.value = shuffleDeck(cards.value);
      peekedId.value = null;
      focusIndex.value = -1;
      if (audioPlayerRef.value && audioPlayerRef.value.playSelectSound) {
        try { audioPlayerRef.value.playSelectSound(); } catch (e) { /* no sound */ }
      }
    };

    // ── Arc Fan geometry + the hover ──
    // The pointer, not the DOM, says which card is lifted: its angle about
    // the pivot becomes a position along the hand (stable at card edges,
    // see stickyIndex), and the lift is a small, soft bell eased by CSS.
    const focusIndex = ref(-1);
    const fan = computed(() => {
      const h = stageH.value || 300;
      const box = slotsBox.value;
      return fanLayout(cards.value.length, {
        width: stageW.value || width.value,
        height: h,
        floor: box ? box.floor : h,
        clear: box ? { halfW: box.halfW, h: box.h } : null,
      });
    });
    // The stack is built around one card (fanZ); it walks toward the lifted
    // card a card per frame, so it re-stacks as a quick ripple, never a pop.
    const zCenter = ref(-1);
    let zRaf = 0;
    const zTick = () => {
      zRaf = 0;
      const target = focusIndex.value;
      if (target < 0 || zCenter.value === target) return;
      // A long way (the pointer just came in at the far end) walks faster,
      // so the ripple is over in a few frames.
      let c = zCenter.value;
      for (let k = Math.max(1, Math.ceil(Math.abs(target - c) / 6)); k > 0; k--) c = stepToward(c, target);
      zCenter.value = c;
      if (zCenter.value !== target) zRaf = requestAnimationFrame(zTick);
    };
    watch(() => cards.value.length, (n) => {
      if (zCenter.value < 0 || zCenter.value > n - 1) zCenter.value = Math.max(0, Math.floor((n - 1) / 2));
    }, { immediate: true });
    watch(focusIndex, (i) => {
      if (i < 0) return;
      if (reducedMotion.value || typeof requestAnimationFrame !== 'function' || zCenter.value < 0) { zCenter.value = i; return; }
      if (!zRaf) zRaf = requestAnimationFrame(zTick);
    });
    const fanStyle = (i) => {
      const f = fan.value;
      const a = f.angles[i] || 0;
      const focus = focusIndex.value;
      const pose = focus < 0 ? { lift: 0, scale: 1 }
        : reducedMotion.value ? (i === focus ? fanHoverPose(0) : { lift: 0, scale: 1 })
          : fanHoverPose(i - focus);
      return {
        width: `${f.cardW}px`,
        height: `${f.cardH}px`,
        marginLeft: `${-f.cardW / 2}px`,
        transformOrigin: `50% ${f.radius}px`,
        top: `${f.floor + f.drop - f.radius}px`,
        '--a': `${a}deg`,
        // pivot → card centre, so the hover scales about the card itself
        '--k': `${f.cardH / 2 - f.radius}px`,
        '--ly': `${pose.lift.toFixed(2)}px`,
        '--s': pose.scale.toFixed(4),
        zIndex: fanZ(i, zCenter.value < 0 ? Math.floor((cards.value.length - 1) / 2) : zCenter.value),
      };
    };
    // One pointer read per frame, however fast the mouse reports.
    let ptrRaf = 0;
    let ptrEv = null;
    const readPointer = () => {
      ptrRaf = 0;
      const ev = ptrEv;
      if (!ev || mode.value !== 'fan' || !stageRef.value) return;
      const r = stageRef.value.getBoundingClientRect();
      const f = fan.value;
      const px = ev.clientX - (r.left + r.width / 2);
      // Measured from the pivot, `drop` px below the fan's floor.
      const py = (r.top + f.floor + f.drop) - ev.clientY;
      const ang = Math.atan2(px, Math.max(1, py)) * 180 / Math.PI;
      // Only over the fan's band: a lifted card is a promise that a click
      // here draws it, so nothing lifts where a click would do nothing.
      const at = onFanBand(f, px, py) ? fanPointerIndex(f.angles, ang) : -1;
      const i = stickyIndex(focusIndex.value, at);
      if (i !== focusIndex.value) focusIndex.value = i;
    };
    const onStagePointer = (ev) => {
      if (mode.value !== 'fan' || !stageRef.value || ev.pointerType === 'touch') return;
      ptrEv = { clientX: ev.clientX, clientY: ev.clientY };
      if (typeof requestAnimationFrame !== 'function') { readPointer(); return; }
      if (!ptrRaf) ptrRaf = requestAnimationFrame(readPointer);
    };
    // A click on the band between slivers still draws the lifted card.
    const onStageClick = (ev) => {
      if (mode.value !== 'fan' || focusIndex.value < 0) return;
      if (ev.target && ev.target.closest && ev.target.closest('.card')) return;
      const card = cards.value[focusIndex.value];
      if (card) drawCard(card, null);
    };
    const onStageLeave = () => {
      if (mode.value !== 'fan') return;
      ptrEv = null;
      focusIndex.value = -1;
    };
    const onStageKey = (ev) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(ev.key) || !cards.value.length) return;
      ev.preventDefault();
      const step = ev.key === 'ArrowRight' ? 1 : -1;
      const next = Math.min(cards.value.length - 1, Math.max(0, (focusIndex.value < 0 ? 0 : focusIndex.value + step)));
      focusIndex.value = next;
      const els = stageRef.value ? stageRef.value.querySelectorAll('.card') : [];
      if (els[next] && els[next].focus) els[next].focus();
      if (mode.value === 'ribbon') centerCard(next);
    };

    // ── Sweeping Spread: a shallow arc that follows the scroll ──
    const scrollLeft = ref(0);
    const ribbonCardH = computed(() => Math.max(70, Math.min(150, (stageH.value || 220) * 0.66)));
    const ribbonCardW = computed(() => ribbonCardH.value * 0.64);
    const ribbonStride = computed(() => Math.max(22, ribbonCardW.value * 0.42));
    const ribbonPad = computed(() => Math.max(0, (stageW.value || width.value) / 2 - ribbonCardW.value / 2));
    const ribbonRailStyle = computed(() => ({
      width: `${ribbonPad.value * 2 + Math.max(0, cards.value.length - 1) * ribbonStride.value + ribbonCardW.value}px`,
      height: '100%',
    }));
    const ribbonStyle = (i, card) => {
      const center = ribbonPad.value + i * ribbonStride.value + ribbonCardW.value / 2;
      const half = (stageW.value || width.value) / 2;
      const dx = center - (scrollLeft.value + half);
      const p = ribbonPose(dx, half);
      const peek = isPeeked(card);
      return {
        left: `${ribbonPad.value + i * ribbonStride.value}px`,
        top: `${Math.max(0, ((stageH.value || 220) - ribbonCardH.value) / 2 + 12)}px`,
        width: `${ribbonCardW.value}px`,
        height: `${ribbonCardH.value}px`,
        transform: peek
          ? `translateY(${p.y - ribbonCardH.value * 0.18}px) rotate(0deg) scale(1.16)`
          : `translateY(${p.y}px) rotate(${p.rot}deg) scale(${p.scale})`,
        zIndex: peek ? 500 : 200 - Math.round(Math.abs(dx) / ribbonStride.value),
      };
    };
    let scrollRaf = 0;
    const onRibbonScroll = () => {
      if (idleHintShown.value || idleTimer) touched();
      if (scrollRaf) return;
      const run = () => { scrollRaf = 0; if (scrollerRef.value) scrollLeft.value = scrollerRef.value.scrollLeft; };
      scrollRaf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(run) : (run(), 0);
    };
    const centerCard = (i, { instant = false } = {}) => {
      const sc = scrollerRef.value;
      if (!sc || typeof sc.scrollTo !== 'function') return;
      const target = ribbonPad.value + i * ribbonStride.value + ribbonCardW.value / 2 - (stageW.value || width.value) / 2;
      sc.scrollTo({ left: Math.max(0, target), behavior: (instant || reducedMotion.value) ? 'auto' : 'smooth' });
      if (instant) scrollLeft.value = sc.scrollLeft;
    };
    // Open on the middle of the deck, once the rail has its width: measured
    // after layout (next frame) and jumped to, not scrolled — a smooth scroll
    // from the left end showed the arc mis-posed for a moment.
    const openOnMiddle = () => {
      const go = () => {
        measure();
        if (mode.value === 'ribbon' && cards.value.length) centerCard(Math.floor(cards.value.length / 2), { instant: true });
      };
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(go)); else go();
    };
    // A mouse in the ribbon (a narrow desktop window) drags to sweep, with a
    // little momentum; touch scrolls natively. A drag never counts as a tap.
    let suppressClick = false;
    let drag = null;
    let glideRaf = 0;
    const cancelGlide = () => { if (glideRaf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(glideRaf); glideRaf = 0; };
    // A touch that moves more than 10px is a scroll, never a tap (recipient polish §1.4).
    let touchStart = null;
    const onTouchUp = (ev) => {
      if (touchIsScroll(touchStart, { x: ev.clientX, y: ev.clientY })) suppressClick = true;
      touchStart = null;
    };
    const onRibbonDown = (ev) => {
      suppressClick = false;
      if (ev.pointerType !== 'mouse') {
        touchStart = { x: ev.clientX, y: ev.clientY };
        window.addEventListener('pointerup', onTouchUp, { once: true });
        return;
      }
      if (!scrollerRef.value) return;
      cancelGlide();
      drag = { x: ev.clientX, start: scrollerRef.value.scrollLeft, lastX: ev.clientX, lastT: performance.now(), v: 0, moved: false };
      window.addEventListener('pointermove', onRibbonMove);
      window.addEventListener('pointerup', onRibbonUp, { once: true });
    };
    const onRibbonMove = (ev) => {
      if (!drag || !scrollerRef.value) return;
      const dx = ev.clientX - drag.x;
      if (Math.abs(dx) > 6) drag.moved = true;
      scrollerRef.value.scrollLeft = drag.start - dx;
      const now = performance.now();
      drag.v = (ev.clientX - drag.lastX) / Math.max(1, now - drag.lastT);
      drag.lastX = ev.clientX; drag.lastT = now;
    };
    const onRibbonUp = () => {
      window.removeEventListener('pointermove', onRibbonMove);
      if (!drag) return;
      suppressClick = drag.moved;
      let v = drag.v;
      drag = null;
      if (reducedMotion.value || typeof requestAnimationFrame !== 'function') return;
      const glide = () => {
        v *= 0.94;
        if (scrollerRef.value) scrollerRef.value.scrollLeft -= v * 16;
        glideRaf = Math.abs(v) > 0.04 ? requestAnimationFrame(glide) : 0;
      };
      glide();
    };

    // ── The deck ──
    const getCardDeck = () => {
      selectedCards.value = [];
      revealedCards.value = [];
      peekedId.value = null;
      focusIndex.value = -1;
      let url = `${static_store.apiUrl}/reading/get-card-deck/?`;
      url += `session_id=${encodeURIComponent(store.session_id)}`;
      axios
        .get(url)
        .then((response) => {
          // Resuming a mid-draw reading: hide the cards already pulled from the
          // deck and seed the running count so the next pick lands at the right
          // absolute card_order (the BE keys off this) and the completion gate
          // (length >= number_of_cards_to_select) trips on the final card.
          // Cards someone else already drew (exclude_card_ids) are never dealt.
          cards.value = dealDeck(response.data.cards, {
            excludeIds: props.exclude_card_ids,
            resumePicked: props.resume_picked,
          });
          if (props.resume_picked && props.resume_picked.length) {
            selectedCards.value = props.resume_picked.map(
              p => ({ card_id: p.card_id, name: p.name })
            );
          }
          // Only override default card-back URL if API returns a valid one
          if (response.data.card_back && response.data.card_back !== cardBackImageUrl.value) {
            backLoaded.value = false;
            cardBackImageUrl.value = response.data.card_back;
          }
          nextTick(() => {
            observe();
            openOnMiddle();
          });
        })
        .catch((error) => console.error(error));
    };
    watch(() => props.exclude_card_ids, (ids) => {
      if (ids && ids.length) cards.value = dealDeck(cards.value, { excludeIds: ids });
    });
    watch(mode, () => { peekedId.value = null; focusIndex.value = -1; nextTick(() => { observe(); openOnMiddle(); }); });

    // Watcher for selectCardsFocusMessage
    watch(
      () => selectCardsFocusMessage.value,
      (newMessage) => {
        if (newMessage && newMessage.trim() !== '') {
          getCardDeck(); // Fetch cards if the focus message is populated
        }
      }
    );
    return {
      cards,
      selectCard,
      undoPick,
      cardBackImageUrl,
      backLoaded,
      onBackLoad,
      onBackError,
      revealedCards,
      showRevealedCards,
      selectedCards,
      audioPlayerRef,
      selectCardsFocusMessage,
      rootRef, stageRef, scrollerRef, trayRef,
      mode, reducedMotion, focusActive, compact,
      drawnCount, restingText, shuffleAllowed, slots, inherited, setSlotRef, heldQuestion, idleHintShown, touched,
      peekedCard, isPeeked, isWords, focusIndex,
      onCardClick, drawCard, shuffle,
      fanStyle, onStagePointer, onStageLeave, onStageKey, onStageClick,
      ribbonStyle, ribbonRailStyle, onRibbonScroll, onRibbonDown,
    };
  },
};
</script>

<style scoped>
.select-cards-wrapper {
  --gold: #FFDFA3;
  --gold-deep: #caa15c;
  --lav: 195, 169, 255;
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  font-family: 'Cormorant Garamond', Georgia, serif;
  color: #e9e0ff;
  background:
    radial-gradient(90% 70% at 50% 105%, rgba(255, 223, 163, .10), transparent 60%),
    radial-gradient(120% 90% at 50% -10%, rgba(124, 58, 237, .22), transparent 60%),
    linear-gradient(180deg, rgba(10, 0, 21, .78), rgba(6, 0, 15, .92));
  box-sizing: border-box;
  z-index: 100;
}

.focus-message {
  flex: 0 0 auto;
  margin: 10px auto 0;
  max-width: min(92%, 680px);
  padding: 8px 18px;
  font-family: 'Cinzel', serif;
  font-size: 0.95rem;
  letter-spacing: .06em;
  line-height: 1.35;
  text-align: center;
  color: var(--gold);
  background: rgba(20, 7, 31, .55);
  border: 1px solid rgba(255, 223, 163, .28);
  border-radius: 999px;
  box-shadow: 0 0 24px rgba(124, 58, 237, .25);
  z-index: 3;
}

/* The gift room's pull (DESIGN-2 §35): the eyebrow over the question, the
   question in quotes, italic, like the card panel's "The question you held". */
.focus-eyebrow {
  flex: 0 0 auto;
  margin: 12px auto -4px;
  font: 600 10px/1.2 'Cinzel', serif;
  letter-spacing: .22em;
  text-transform: uppercase;
  text-align: center;
  color: #d9b86a;
  z-index: 3;
}
.focus-message.held {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-weight: 500;
  font-size: 1.15rem;
  letter-spacing: .01em;
  color: #f5e6c0;
  padding: 8px 22px;
  background: transparent;
  border-color: rgba(217, 184, 106, .45);
  box-shadow: none;
}
.mode-fan .focus-message.held { font-size: 1.3rem; }
.idle-hint {
  margin: 0 0 2px;
  font: italic 500 14px/1.3 'Cormorant Garamond', Georgia, serif;
  color: #b9a6d6;
  animation: idle-in .8s ease both;
}
@keyframes idle-in { from { opacity: 0; } to { opacity: 1; } }
.tray-sep { align-self: center; color: #d9b86a; font-size: 13px; margin: 0 -4px 12px; }
/* Their slot is named as signed ("Mom & Dad", at most 12 characters): never cut. */
.quiet .slot-label { color: #d9b86a; left: -28px; right: -28px; overflow: visible; text-overflow: clip; }
/* A lone slot has no neighbour to touch: its label ("Your card") runs past
   the slot rather than cut to "YOUR C…" at 390 (lesson draw walk 2 #1). */
.lone .slot-label { left: -40px; right: -40px; overflow: visible; text-overflow: clip; }
@media (prefers-reduced-motion: reduce) { .idle-hint { animation: none; } }

.picker-stage {
  position: relative;
  flex: 1 1 auto;
  min-height: 110px;
  overflow: hidden;
  outline: none;
}

.ambient-sigil {
  position: absolute;
  left: 50%;
  bottom: -110px;
  width: 260px;
  height: 260px;
  transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 223, 163, .20), rgba(124, 58, 237, .12) 40%, transparent 70%);
  filter: blur(8px);
  pointer-events: none;
  animation: sigil-pulse 5s ease-in-out infinite;
}
.mode-ribbon .ambient-sigil { bottom: auto; top: 50%; transform: translate(-50%, -50%); width: 200px; height: 200px; }
@keyframes sigil-pulse { 0%, 100% { opacity: .6; } 50% { opacity: 1; } }

.card {
  position: absolute;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(255, 223, 163, .35);
  box-shadow: 0 8px 22px -8px rgba(0, 0, 0, .85);
  background: #160b26;
  -webkit-tap-highlight-color: transparent;
  touch-action: pan-x;
  user-select: none;
  will-change: transform;
}
.card::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px rgba(255, 223, 163, .0), 0 0 0 rgba(255, 223, 163, 0);
  transition: box-shadow .35s ease;
  pointer-events: none;
}
/* N3 (2026-11-02): the painted placeholder back while the back image loads
   (or when none loads): a gold frame and a star on deep violet, never an
   empty outline. Paint only: the card's size and place are untouched. */
.card.back-loading,
.tray-slot.back-loading {
  background-color: #1d0f33;
  background-image:
    radial-gradient(circle at 50% 50%, rgba(255, 223, 163, .55) 0 3%, rgba(255, 223, 163, 0) 4%),
    radial-gradient(circle at 50% 50%, rgba(255, 223, 163, 0) 0 17%, rgba(255, 223, 163, .35) 18% 19%, rgba(255, 223, 163, 0) 20%),
    repeating-linear-gradient(45deg, rgba(255, 223, 163, .07) 0 2px, transparent 2px 10px),
    repeating-linear-gradient(-45deg, rgba(255, 223, 163, .07) 0 2px, transparent 2px 10px),
    linear-gradient(160deg, #2a1748 0%, #160b26 100%);
  outline: 1px solid rgba(255, 223, 163, .45);
  outline-offset: -5px;
}
.card-back {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  pointer-events: none;
}
.card:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }

/* Arc Fan */
.fan { position: absolute; inset: 0; left: 50%; width: 0; }
/* The arch's legs come down beside the tray, past the stage's floor: the
   stage lets them show (the fan never leaves its own box; card-picker.js
   keeps every corner inside it), and the tray lets the pointer through to
   them. */
.picker-stage.mode-fan { overflow: visible; }
.mode-fan .picker-foot { pointer-events: none; }
.mode-fan .picker-foot button { pointer-events: auto; }
/* The hover: a small rise along the card's own axis and a slight grow about
   its centre (transform only), the glow faded in (opacity only). One curve,
   one duration (FAN_HOVER in card-picker.js). */
.fan .card {
  /* The layout's size is the card's whole size, border included, so its
     corners land exactly where card-picker.js puts them. */
  box-sizing: border-box;
  transform:
    rotate(var(--a, 0deg))
    translateY(calc(var(--k, 0px) - var(--ly, 0px)))
    scale(var(--s, 1))
    translateY(calc(-1 * var(--k, 0px)));
  transition: transform .22s cubic-bezier(.2, .8, .2, 1);
}
.fan .card::after {
  box-shadow: inset 0 0 0 1px rgba(255, 223, 163, .9), inset 0 0 18px rgba(255, 223, 163, .4);
  opacity: 0;
  transition: opacity .22s cubic-bezier(.2, .8, .2, 1);
}
.fan .card.lift::after { opacity: 1; }

/* Sweeping Spread */
.ribbon-scroller {
  position: absolute;
  inset: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
}
.ribbon-scroller::-webkit-scrollbar { display: none; }
.ribbon-rail { position: relative; }
.ribbon-rail .card {
  scroll-snap-align: center;
  transition: transform .28s cubic-bezier(.2, .85, .25, 1);
}
.ribbon-rail .card.peek {
  border-color: rgba(255, 223, 163, .95);
  box-shadow: 0 0 30px rgba(255, 223, 163, .65), 0 18px 28px rgba(124, 58, 237, .8);
}
.peek-draw {
  min-height: 44px;
  padding: 0 22px;
  border-radius: 999px;
  border: 1px solid rgba(255, 223, 163, .7);
  background: linear-gradient(180deg, #fff3d6, var(--gold));
  color: #1a0e2e;
  font-family: 'Cinzel', serif;
  font-weight: 700;
  font-size: .85rem;
  letter-spacing: .08em;
  box-shadow: 0 6px 24px rgba(255, 223, 163, .4);
  cursor: pointer;
  animation: peek-in .25s ease-out both;
}
@keyframes peek-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }

/* Tray + status */
.picker-foot {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 4px 10px calc(8px + env(safe-area-inset-bottom, 0px));
  z-index: 3;
}
.tray { display: flex; justify-content: center; align-items: flex-end; gap: 16px; max-width: 100%; }
.tray-group { display: flex; flex-direction: column; align-items: center; gap: 3px; min-width: 0; }
.tray-group-label {
  font-family: 'Cinzel', serif;
  font-size: 9px;
  letter-spacing: .26em;
  text-transform: uppercase;
  color: var(--gold-deep);
  white-space: nowrap;
}
.tray-row { display: flex; gap: 8px; }
.tray-slot, .tray-card {
  position: relative;
  width: var(--tray-w, 62px);
  height: calc(var(--tray-w, 62px) * 1.5);
  margin: 0;
  border-radius: 8px;
  box-sizing: border-box;
  flex: 0 0 auto;
}
.tray-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed rgba(255, 223, 163, .3);
  background: rgba(255, 255, 255, .02);
  color: rgba(255, 223, 163, .4);
  transition: border-color .4s ease, box-shadow .4s ease;
}
.tray-slot.filled {
  border: 1px solid rgba(255, 223, 163, .7);
  box-shadow: 0 0 20px rgba(255, 223, 163, .3), 0 10px 26px -10px rgba(124, 58, 237, .7);
  animation: tray-pop .55s cubic-bezier(.2, 1.3, .3, 1) both;
}
.tray-slot img, .tray-card img { width: 100%; height: 100%; object-fit: cover; border-radius: 7px; display: block; }
.slot-star { font-size: 20px; }
.slot-label, .tray-card small {
  position: absolute;
  /* Within half the row's gap on each side: two labels never touch
     ("THE SURF.THE TRU..", gift bug walk 2026-10-25 #30). */
  left: -3px;
  right: -3px;
  bottom: -14px;
  font-family: 'Cinzel', serif;
  font-size: 8px;
  letter-spacing: .08em;
  text-transform: uppercase;
  text-align: center;
  color: rgba(var(--lav), .8);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.has-inherited .tray-row, .tray-group:has(.slot-label) .tray-row { margin-bottom: 12px; }
/* A name of more than one word ("What helps") may take a second line, split
   only between words: never cut mid-word (lesson draw walk 2 #2). */
.slot-label.words {
  bottom: auto;
  /* level with its one-line neighbours (their line box is taller) */
  top: calc(100% + 4px);
  line-height: 1.15;
  white-space: normal;
  overflow-wrap: normal;
  word-break: normal;
  hyphens: none;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
/* (as specific as the :has rule above, which it must outrank) */
.tray-group:has(.slot-label) .tray-row.two-line { margin-bottom: 22px; }
.tray-card.inherited {
  border: 1px solid rgba(255, 223, 163, .85);
  box-shadow: 0 0 18px rgba(255, 223, 163, .35);
  background: #241338;
}
.tray-card.inherited.reversed img { transform: rotate(180deg); }
.tray-card figcaption {
  position: absolute;
  left: 0; right: 0; top: 2px;
  font-family: 'Cinzel', serif;
  font-size: 7.5px;
  letter-spacing: .06em;
  text-align: center;
  color: #fff3d6;
  text-shadow: 0 1px 3px #000, 0 0 6px #000;
  padding: 2px 2px 8px;
  border-radius: 7px 7px 0 0;
  background: linear-gradient(180deg, rgba(10, 0, 21, .88), rgba(10, 0, 21, .55) 70%, transparent);
}
.tray-card figcaption { top: 0; }
/* The full name, between words only ("The Magicia", walk 2026-11-03 D). */
.tray-card figcaption { line-height: 1.15; overflow-wrap: normal; word-break: keep-all; hyphens: none; }
/* An inherited card's place ("The Surroundings") may take a second line,
   split between words, never an ellipsis; small capitals (Cinzel), not full
   capitals, so one long word fits its card (walk 2026-11-03 D). */
/* Centred under its card and a little wider than it, so one long word
   ("Surroundings") stays whole and centred rather than spilling right. */
.tray-card small { text-transform: none; letter-spacing: .02em; white-space: normal; overflow: visible; text-overflow: clip; line-height: 1.15; bottom: auto; top: calc(100% + 4px);
  overflow-wrap: normal; word-break: keep-all; hyphens: none;
  left: 50%; right: auto; width: max-content; max-width: calc(var(--tray-w, 62px) + 16px); transform: translateX(-50%); }
.has-inherited .tray-group:has(.tray-card small) .tray-row { margin-bottom: 22px; }
@keyframes tray-pop { 0% { transform: translateY(-18px) scale(.75); opacity: 0; } 100% { transform: none; opacity: 1; } }

.picker-status {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 32px;
}
.pips { display: inline-flex; gap: 6px; }
.pip {
  width: 10px; height: 10px; border-radius: 50%;
  border: 1px solid rgba(255, 223, 163, .55);
  transition: all .4s cubic-bezier(.2, .8, .2, 1);
}
.pip.filled {
  background: radial-gradient(circle at 50% 35%, #fff6df, var(--gold) 70%);
  box-shadow: 0 0 12px rgba(255, 223, 163, .8);
  transform: scale(1.15);
}
.count-text { font-style: italic; font-size: 15px; color: rgba(var(--lav), .9); }
.count-text b { color: var(--gold); font-style: normal; }
/* Resting between picks: the deck dims and waits; the count line says why. */
.picker-stage { transition: opacity .35s ease, filter .35s ease; }
.resting .picker-stage { opacity: .45; filter: saturate(.7); cursor: default; }
.resting .picker-stage .card { pointer-events: none; }
.resting .count-text { color: var(--gold); }
.shuffle {
  min-height: 32px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 223, 163, .35);
  background: transparent;
  color: rgba(255, 223, 163, .85);
  font-family: 'Cinzel', serif;
  font-size: 10px;
  letter-spacing: .2em;
  text-transform: uppercase;
  cursor: pointer;
}
.shuffle:hover { background: rgba(255, 223, 163, .08); color: var(--gold); }

/* Desktop: a roomier tray */
.mode-fan .picker-foot { --tray-w: 64px; }
.mode-fan .focus-message { font-size: 1.05rem; }

/* Phones: everything tighter so the deck keeps its room */
@media (max-width: 480px) {
  .focus-message { margin-top: 6px; font-size: .82rem; padding: 6px 12px; border-radius: 14px; }
  .picker-foot { --tray-w: 40px; gap: 2px; }
  .tray { gap: 10px; }
  .tray-row { gap: 5px; }
  .tray-card figcaption { font-size: 6px; letter-spacing: 0; padding: 2px 0 8px; }
  /* One word a line on a phone: two places side by side never run together. */
  .tray-card small { font-size: 6.5px; letter-spacing: 0; width: min-content; }
  .count-text { font-size: 13px; }
  .shuffle { min-height: 44px; }
  .slot-star { font-size: 14px; }
}
@media (max-height: 520px) and (min-width: 481px) {
  .picker-foot { --tray-w: 40px; }
}

/* A short pane (the first iPhone SE gives the picker ~240px): the tray and
   its status share one row, so the deck keeps the height it needs. */
.compact .focus-message { margin-top: 4px; padding: 4px 12px; font-size: .78rem; }
.compact .picker-foot { flex-direction: row; justify-content: center; gap: 10px; padding: 2px 8px 6px; --tray-w: 32px; }
.compact .tray-group-label { font-size: 7.5px; letter-spacing: .14em; }
.compact .slot-label, .compact .tray-card small { font-size: 6.5px; bottom: -11px; }
.compact .tray-row { margin-bottom: 11px; gap: 4px; }
.compact .tray-group:has(.slot-label) .tray-row.two-line { margin-bottom: 18px; }
.compact .slot-star { font-size: 12px; }
.compact .pips, .compact .count-word { display: none; }
.compact .picker-status { gap: 8px; }
.compact .count-text { font-size: 13px; white-space: nowrap; }
.compact .shuffle { padding: 0 10px; letter-spacing: .12em; }
.compact .peek-draw { padding: 0 16px; font-size: .78rem; }

/* Reduced motion: no ripple travel, no pulse, no pops — the same choices, still. */
.reduced .card, .reduced .card::after, .reduced .tray-slot.filled, .reduced .pip { transition: none !important; animation: none !important; }
.reduced .ambient-sigil { animation: none; }
.reduced .picker-stage { transition: none; }
@media (prefers-reduced-motion: reduce) {
  .card, .card::after, .tray-slot.filled, .pip, .ambient-sigil { transition: none !important; animation: none !important; }
}
</style>
